import React, { useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import {
    CHECKLIST_ETAT,
    CHECKLIST_COLORS,
    countChecklistEtats,
    getChecklistElementsEnDefaut,
} from '../../../modules/conteneurMap/conteneurMap.helpers';

const NAVY = '#213067';

// Les libellés de commune sont hétérogènes en base (« ST SEBASTIEN SUR LOIRE » / « SAINT-SEBASTIEN-SUR-LOIRE »,
// suffixe DALIM…) : on regroupe sur une clé normalisée — même règle que le rapport PDF (commune_key).
const SMALL = new Set(['SUR', 'DE', 'DU', 'LA', 'LE', 'LES', 'EN']);
const communeKey = (s) => (s || '—').trim().replace(/\s+DALIM$/i, '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase()
    .replace(/[^A-Z]+/g, ' ').trim().replace(/^ST /, 'SAINT ').replace(/^STE /, 'SAINTE ');
const titleCase = (s) => {
    const w = communeKey(s).split(' ').filter(Boolean);
    if (w.length === 0) return '—';
    const parts = w.map((x, i) => (i > 0 && SMALL.has(x) ? x.toLowerCase() : x.charAt(0) + x.slice(1).toLowerCase()));
    return ['La', 'Le', 'Les'].includes(parts[0]) ? `${parts[0]} ${parts.slice(1).join('-')}` : parts.join('-');
};

const Bar = ({ value, max, color }) => (
    <Box sx={{ height: 7, borderRadius: 4, bgcolor: 'rgba(33,48,103,.08)', overflow: 'hidden' }}>
        <Box sx={{ width: `${max ? (value / max) * 100 : 0}%`, height: '100%', bgcolor: color, borderRadius: 4 }} />
    </Box>
);

const Row = ({ label, value, max, color, suffix }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 56px', alignItems: 'center', columnGap: 1, rowGap: 0.25, mb: 0.6 }}>
        <Typography variant="caption" sx={{ color: '#3a4252', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: NAVY, fontWeight: 700, textAlign: 'right', lineHeight: 1.2 }}>{value}{suffix}</Typography>
        <Box sx={{ gridColumn: '1 / span 2' }}><Bar value={value} max={max} color={color} /></Box>
    </Box>
);

/**
 * Indicateurs du mode Maintenance (PAT-04), superposés à la carte.
 * `rows` = PAV des patrimoines cochés (plage / acte courants), AVANT filtre d'état : les dénominateurs
 * restent ceux du parc sélectionné même quand on masque les RAS. Un Conteneur = un PAV (`quantite` = abri-bacs).
 */
function ChecklistKpiPanel({ rows, presentation = false }) {
    const [open, setOpen] = useState(true);

    const kpi = useMemo(() => {
        const counts = countChecklistEtats(rows);
        const parc = rows.length;
        const visites = counts.RAS + counts.PROBLEME + counts.TENTATIVE_IMPOSSIBLE;
        const inspectes = counts.RAS + counts.PROBLEME;
        const bacs = rows.reduce((s, r) => s + (Number(r.quantite) || 0), 0);
        const photos = rows.reduce((s, r) => s + (Number(r.checklist?.nbPhotos) || 0), 0);
        const elements = new Map();
        const communes = new Map();
        rows.forEach((r) => {
            const c = r.checklist;
            const com = titleCase(r.commune);
            if (!communes.has(com)) communes.set(com, { insp: 0, def: 0 });
            if (c?.etat === CHECKLIST_ETAT.RAS || c?.etat === CHECKLIST_ETAT.PROBLEME) communes.get(com).insp += 1;
            if (c?.etat === CHECKLIST_ETAT.PROBLEME) {
                communes.get(com).def += 1;
                getChecklistElementsEnDefaut(c).forEach((e) => {
                    const k = e.libelle ?? e.code;
                    elements.set(k, (elements.get(k) || 0) + 1);
                });
            }
        });
        const topElements = Array.from(elements.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5);
        const topCommunes = Array.from(communes.entries()).filter(([, v]) => v.def > 0)
            .sort((a, b) => b[1].def - a[1].def).slice(0, 5);
        return { counts, parc, visites, inspectes, bacs, photos, topElements, topCommunes };
    }, [rows]);

    if (!rows || rows.length === 0) return null;
    const pct = (n, d) => (d ? `${Math.round((n / d) * 100)} %` : '—');
    const fs = presentation ? 1.08 : 1;

    return (
        <Box sx={{
            position: 'absolute', left: 12, bottom: 24, zIndex: 1000, width: open ? 290 * fs : 'auto',
            bgcolor: 'rgba(255,255,255,.94)', backdropFilter: 'blur(3px)', borderRadius: 2,
            boxShadow: '0 4px 18px rgba(33,48,103,.18)', border: '1px solid rgba(33,48,103,.12)',
            fontSize: `${fs}em`, transition: 'width .2s',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.5, borderBottom: open ? '1px solid rgba(33,48,103,.1)' : 'none' }}>
                <Typography variant="subtitle2" sx={{ color: NAVY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.02em', fontSize: '.78em' }}>
                    Indicateurs · {kpi.parc} PAV
                </Typography>
                <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? 'Replier' : 'Déplier'}>
                    {open ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                </IconButton>
            </Box>
            {open && (
                <Box sx={{ px: 1.5, py: 1 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 1 }}>
                        <Box>
                            <Typography sx={{ color: NAVY, fontWeight: 700, fontSize: '1.45em', lineHeight: 1 }}>{pct(kpi.visites, kpi.parc)}</Typography>
                            <Typography variant="caption" sx={{ color: '#5b6475', lineHeight: 1.15, display: 'block' }}>couverture<br />{kpi.visites} / {kpi.parc} PAV</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ color: CHECKLIST_COLORS.PROBLEME_2, fontWeight: 700, fontSize: '1.45em', lineHeight: 1 }}>{kpi.counts.PROBLEME}</Typography>
                            <Typography variant="caption" sx={{ color: '#5b6475', lineHeight: 1.15, display: 'block' }}>en défaut<br />{pct(kpi.counts.PROBLEME, kpi.inspectes)} des inspectés</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ color: CHECKLIST_COLORS.TENTATIVE_IMPOSSIBLE, fontWeight: 700, fontSize: '1.45em', lineHeight: 1 }}>{kpi.counts.TENTATIVE_IMPOSSIBLE}</Typography>
                            <Typography variant="caption" sx={{ color: '#5b6475', lineHeight: 1.15, display: 'block' }}>impossibles<br />{kpi.photos} photos</Typography>
                        </Box>
                    </Box>
                    <Bar value={kpi.visites} max={kpi.parc} color={CHECKLIST_COLORS.RAS} />
                    <Typography variant="caption" sx={{ color: '#5b6475', display: 'block', mt: 0.25, mb: 1 }}>
                        {kpi.counts.RAS} RAS · {kpi.counts.PROBLEME} défaut{kpi.counts.PROBLEME > 1 ? 's' : ''} · {kpi.counts.TENTATIVE_IMPOSSIBLE} impossible{kpi.counts.TENTATIVE_IMPOSSIBLE > 1 ? 's' : ''} · {kpi.counts.NON_INSPECTE} à venir · {kpi.bacs} abri-bacs
                    </Typography>

                    {kpi.topElements.length > 0 && (
                        <>
                            <Typography variant="caption" sx={{ color: NAVY, fontWeight: 700, display: 'block', mb: 0.5 }}>Défauts par point de contrôle</Typography>
                            {kpi.topElements.map(([k, v]) => (
                                <Row key={k} label={k} value={v} max={kpi.topElements[0][1]} color={CHECKLIST_COLORS.PROBLEME_2} suffix="" />
                            ))}
                        </>
                    )}
                    {kpi.topCommunes.length > 0 && (
                        <>
                            <Typography variant="caption" sx={{ color: NAVY, fontWeight: 700, display: 'block', mt: 1, mb: 0.5 }}>Communes les plus touchées</Typography>
                            {kpi.topCommunes.map(([k, v]) => (
                                <Row key={k} label={`${k} · ${pct(v.def, v.insp)}`} value={v.def} max={kpi.topCommunes[0][1].def} color={CHECKLIST_COLORS.PROBLEME_3} suffix="" />
                            ))}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default ChecklistKpiPanel;
